declare global {
  interface Window {
      _invoke_: Function;
      vuplex: {
        postMessage: Function;
        addEventListener: Function;
      }
  }
}

import "focus-visible"
import { NEVER, Subject, defer, merge } from "rxjs"
import {
  delay,
  filter,
  map,
  mergeWith,
  shareReplay,
  switchMap
} from "rxjs/operators"

import { configuration, feature } from "./_"
import {
  at,
  getElement,
  requestJSON,
  setToggle,
  watchDocument,
  watchKeyboard,
  watchLocation,
  watchLocationTarget,
  watchMedia,
  watchPrint,
  watchViewport
} from "./browser"
import {
  getComponentElement,
  getComponentElements,
  mountBackToTop,
  mountContent,
  mountDialog,
  mountHeader,
  mountHeaderTitle,
  mountPalette,
  mountSearch,
  mountSearchHiglight,
  mountSidebar,
  mountSource,
  mountTableOfContents,
  mountTabs,
  watchHeader,
  watchMain
} from "./components"
import {
  SearchIndex,
  setupClipboardJS,
  setupInstantLoading,
  setupVersionSelector
} from "./integrations"
import {
  patchIndeterminate,
  patchScrollfix,
  patchScrolllock
} from "./patches"

/* ----------------------------------------------------------------------------
 * Application
 * ------------------------------------------------------------------------- */

/* Yay, JavaScript is available */
document.documentElement.classList.remove("no-js")
document.documentElement.classList.add("js")

/* Set up navigation observables and subjects */
const document$ = watchDocument()
const location$ = watchLocation()
const target$   = watchLocationTarget()
const keyboard$ = watchKeyboard()

/* Set up media observables */
const viewport$ = watchViewport()
const tablet$   = watchMedia("(min-width: 960px)")
const screen$   = watchMedia("(min-width: 1220px)")
const print$    = watchPrint()

/* Retrieve search index, if search is enabled */
const config = configuration()
const index$ = document.forms.namedItem("search")
  ? __search?.index || requestJSON<SearchIndex>(
    new URL("search/search_index.json", config.base)
  )
  : NEVER

/* Set up Clipboard.js integration */
const alert$ = new Subject<string>()
setupClipboardJS({ alert$ })

/* Set up instant loading, if enabled */
if (feature("navigation.instant"))
  setupInstantLoading({ document$, location$, viewport$ })

/* Set up version selector */
if (config.version?.provider === "mike")
  setupVersionSelector()

/* Always close drawer and search on navigation */
merge(location$, target$)
  .pipe(
    delay(125)
  )
    .subscribe(() => {
      setToggle("drawer", false)
      setToggle("search", false)
    })

/* Set up global keyboard handlers */
keyboard$
  .pipe(
    filter(({ mode }) => mode === "global")
  )
    .subscribe(key => {
      switch (key.type) {

        /* Go to previous page */
        case "p":
        case ",":
          const prev = getElement("[href][rel=prev]")
          if (typeof prev !== "undefined")
            prev.click()
          break

        /* Go to next page */
        case "n":
        case ".":
          const next = getElement("[href][rel=next]")
          if (typeof next !== "undefined")
            next.click()
          break
      }
    })

/* Set up patches */
patchIndeterminate({ document$, tablet$ })
patchScrollfix({ document$ })
patchScrolllock({ viewport$, tablet$ })

/* Set up header and main area observable */
const header$ = watchHeader(getComponentElement("header"), { viewport$ })
const main$ = document$
  .pipe(
    map(() => getComponentElement("main")),
    switchMap(el => watchMain(el, { viewport$, header$ })),
    shareReplay(1)
  )

/* Set up control component observables */
const control$ = merge(

  /* Dialog */
  ...getComponentElements("dialog")
    .map(el => mountDialog(el, { alert$ })),

  /* Header */
  ...getComponentElements("header")
    .map(el => mountHeader(el, { viewport$, header$, main$ })),

  /* Color palette */
  ...getComponentElements("palette")
    .map(el => mountPalette(el)),

  /* Search */
  ...getComponentElements("search")
    .map(el => mountSearch(el, { index$, keyboard$ })),

  /* Repository information */
  ...getComponentElements("source")
    .map(el => mountSource(el))
)

/* Set up content component observables */
const content$ = defer(() => merge(

  /* Content */
  ...getComponentElements("content")
    .map(el => mountContent(el, { target$, viewport$, print$ })),

  /* Search highlighting */
  ...getComponentElements("content")
    .map(el => feature("search.highlight")
      ? mountSearchHiglight(el, { index$, location$ })
      : NEVER
    ),

  /* Header title */
  ...getComponentElements("header-title")
    .map(el => mountHeaderTitle(el, { viewport$, header$ })),

  /* Sidebar */
  ...getComponentElements("sidebar")
    .map(el => el.getAttribute("data-md-type") === "navigation"
      ? at(screen$, () => mountSidebar(el, { viewport$, header$, main$ }))
      : at(tablet$, () => mountSidebar(el, { viewport$, header$, main$ }))
    ),

  /* Navigation tabs */
  ...getComponentElements("tabs")
    .map(el => mountTabs(el, { viewport$, header$ })),

  /* Table of contents */
  ...getComponentElements("toc")
    .map(el => mountTableOfContents(el, { viewport$, header$ })),

  /* Back-to-top button */
  ...getComponentElements("top")
    .map(el => mountBackToTop(el, { viewport$, header$, main$ }))
))

/* Set up component observables */
const component$ = document$
  .pipe(
    switchMap(() => content$),
    mergeWith(control$),
    shareReplay(1)
  )

/* Subscribe to all components */
component$.subscribe()


/* ----------------------------------------------------------------------------
 * Exports
 * ------------------------------------------------------------------------- */

window.document$  = document$          /* Document observable */
window.location$  = location$          /* Location subject */
window.target$    = target$            /* Location target observable */
window.keyboard$  = keyboard$          /* Keyboard observable */
window.viewport$  = viewport$          /* Viewport observable */
window.tablet$    = tablet$            /* Tablet observable */
window.screen$    = screen$            /* Screen observable */
window.print$     = print$             /* Print mode observable */
window.alert$     = alert$             /* Alert subject */
window.component$ = component$         /* Component observable */

function setCookie(cname: string, cvalue: string, exdays: number) {
  let d = new Date();
  d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
  const expires = `expires=${d.toUTCString()}`;
  document.cookie = `${cname}=${cvalue};${expires};path=/`;
}

function getCookie(cname: string)
{
  let name = cname + "=";
  let ca = document.cookie.split(';');
  for(let i=0; i<ca.length; i++) {
    let c = ca[i].trim();
    if (c.indexOf(name)==0) return c.substring(name.length,c.length);
  }
  return "";
}

const postMessage = (body: any) => {
  console.info('Post message:', JSON.stringify(body));

  if (window && window.vuplex) {
      window.vuplex?.postMessage(body);
  } else {
      console.error('no vuplex in window object after initialized');
  }
}

window.addEventListener('DOMContentLoaded', function () {
  let from = new URLSearchParams(document.location.search).get('from') ?? getCookie('doc_from');
  let footerEl = document.getElementsByClassName('home_footer ')[0] as HTMLElement;

  if (from === 'desktop') {

    try {
      window._invoke_('document_ready', {}, () => { });
    } catch (err) {
      console.error(err);
    }

    setCookie('doc_from', 'desktop', 1);
    footerEl.style.display = 'none';
  } else if (from === 'studio') {

    setCookie('doc_from', 'studio', 1);
    footerEl.style.display = 'none';
  }
});

window.addEventListener('contextmenu', function(e:Event) {
    if (getCookie('doc_from') == 'desktop') {
      e.preventDefault();
      return;
    } else if (getCookie('doc_from') == 'studio') {
      e.preventDefault();
      return;
    } else {
      return;
    }
});

Array.from(document.getElementsByTagName('a')).forEach(el => {
  el.onclick = function (e: Event) {

    const link = el.href;
    const from = getCookie('doc_from');
    console.log('link = ', link);
    if (from === 'desktop' || from === 'studio') {
      if (/^((https\:\/\/)?developer(-int|-stg)?.yahaha.com(\/.*|)|(\.{1,2}\/.*))$/.test(link) || /^(http:\/\/localhost:8000(\/.*|)|(\.{1,2}\/.*))$/.test(link)) {
        console.log('/^$/.test(link)');
        return;
      } else {
        e.preventDefault();
        e.stopPropagation();
        if (window && window._invoke_) {
          console.log('window._invoke_');
          //desktop
          if (from === 'desktop')
            window._invoke_('launch_url_external', { 'url': link }, function () { });
          //studio
          if (from === 'studio')
            window._invoke_({type: 'yahaha.embed.openUrl', url: link})
          return;
        }
        if (window && window.vuplex) {
          console.log('window.vuplex');
          window.vuplex?.postMessage({
            type: 'yahaha.embed.openUrl',
            url: link
          });
          return;
        }
        console.log('window.open(link);');
        window.open(link);
        return;
      }
    }
    console.log('return true;');
    return true;
  }
})